import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import SqrtInput from "../../../../components/inputs/SqrtInput";

// ==== FUNKCJE POMOCNICZE ====

function evaluateExpression(expr) {
  const cleaned = String(expr ?? "")
    .replace(/\s/g, "")
    .replace(/\u200b/g, "")
    .replace(/[\u0000-\u001F\u007F-\u009F]/g, "")
    .replace(/\\\\sqrt\{([\d.,]+)\}/g, "√$1")
    .replace(/\\sqrt\{([\d.,]+)\}/g, "√$1")
    .replace(/sqrt\{([\d.,]+)\}/g, "√$1")
    .replace(/sqrt/g, "√")
    .replace(/√\{([^}]+)\}/g, "√$1")
    .replace(/√\(([^)]+)\)/g, "√$1")
    .replace(/,/g, "."); // przecinki -> kropki

  try {
    const parsed = cleaned
      .replace(/(\d*)√(\d+(\.\d+)?)/g, (_, a, b) => `${a || "1"}*Math.sqrt(${b})`)
      .replace(/√(\d+(\.\d+)?)/g, (_, b) => `Math.sqrt(${b})`);

    return Function(`return ${parsed}`)();
  } catch {
    return NaN;
  }
}

function normalizeCanonical(str) {
  return String(str ?? "")
    .replace(/\s/g, "")
    .replace(/\u200b/g, "")
    .replace(/[\u0000-\u001F\u007F-\u009F]/g, "")
    .replace(/\\√/g, "√")
    .replace(/\\\\sqrt/g, "√")
    .replace(/\\sqrt/g, "√")
    .replace(/sqrt/g, "√")
    .replace(/√\{([^}]+)\}/g, "√$1")
    .replace(/√\(([^)]+)\)/g, "√$1")
    .replace(/^1√/, "√")
    .replace(/√0*(\d+)/g, (_, d) => `√${parseInt(d, 10)}`)
    .replace(/^0*(\d+)$/, (_, d) => `${parseInt(d, 10)}`)
    .trim();
}

export function simplifySqrt(n) {
  let factor = 1;
  for (let i = Math.floor(Math.sqrt(n)); i >= 2; i--) {
    if (n % (i * i) === 0) {
      factor = i;
      break;
    }
  }
  const outside = factor;
  const inside = n / (factor * factor);

  if (inside === 1) return `${outside}`;
  if (outside === 1) return `\\sqrt{${inside}}`;
  return `${outside}\\sqrt{${inside}}`;
}

function coerceToString(val) {
  if (val == null) return "";
  if (typeof val === "string") return val;
  if (typeof val === "number" || typeof val === "boolean") return String(val);
  if (typeof val === "object" && val?.target && "value" in val.target) {
    return String(val.target.value ?? "");
  }
  if (typeof val === "object" && "raw" in val) return String(val.raw ?? "");
  if (typeof val === "object" && "latex" in val) return String(val.latex ?? "");
  return "";
}

// ==== GENERATOR ====

export function generateTask() {
  const losuj = () => Math.floor(Math.random() * 19) - 9;
  let x1, y1, x2, y2;
  do {
    x1 = losuj();
    y1 = losuj();
    x2 = losuj();
    y2 = losuj();
  } while (x1 === x2 && y1 === y2);

  const dx = x2 - x1;
  const dy = y2 - y1;
  const dx2 = dx ** 2;
  const dy2 = dy ** 2;
  const pierw = dx2 + dy2;
  const sqrt = simplifySqrt(pierw);

  return {
    type: "LengthTask",
    data: {
      x1,
      y1,
      x2,
      y2,
      sqrt: String(sqrt),
    },
  };
}

// ==== RENDER ====

export function renderTask({ data }) {
  const { x1, y1, x2, y2 } = data;
  return (
    <>
      <p>
        Oblicz długość odcinka <InlineMath math="AB" />, którego końcami są punkty:
      </p>
      <p>
        <InlineMath math={`A=(${x1},${y1})`} />, <InlineMath math={`B=(${x2},${y2})`} />
      </p>
    </>
  );
}

export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <SqrtInput value={coerceToString(userAnswer)} onChange={onChange} disabled={disabled} />
  );
}

// ==== SPRAWDZENIE ====

export function checkAnswer({ data }, userAnswer) {
  const ua = coerceToString(userAnswer);
  if (!ua) return false;

  const expectedSimplified = String(data.sqrt || "");
  const expectedNorm = normalizeCanonical(expectedSimplified);
  const givenNorm = normalizeCanonical(ua);

  const expectedVal = evaluateExpression(expectedNorm);
  const givenVal = evaluateExpression(givenNorm);

  const isClose =
    Number.isFinite(expectedVal) &&
    Number.isFinite(givenVal) &&
    Math.abs(expectedVal - givenVal) < 0.01;

  if (!isClose) return false;

  // 🔁 Znormalizowane porównanie bez kropek i przecinków
  const removeDotsAndCommas = (s) => s.replace(/[.,]/g, "");
  const normA = removeDotsAndCommas(expectedNorm);
  const normB = removeDotsAndCommas(givenNorm);

  // jeśli zapis identyczny (po normalizacji) -> pełna poprawność
  if (normA === normB) return true;

  // policz "przed" = √(dx^2+dy^2) dla tego konkretnego wariantu
  const { x1, y1, x2, y2 } = data;
  const dx = x2 - x1;
  const dy = y2 - y1;
  const pierw = dx * dx + dy * dy;

  const beforeLatex = `\\sqrt{${pierw}}`;
  const afterLatex = expectedSimplified;

  // jeśli pierwiastek da się uprościć, pokaż konkretny komunikat:
  // "√8 = 2√2". Jeśli się nie da (np. √35), to nie spamuj komunikatem.
  const canSimplify = simplifySqrt(pierw) !== `\\sqrt{${pierw}}`;

  if (canSimplify) {
    return {
      correct: true,
      needsSimplification: true,
      expected: String(data.sqrt || ""),
      messageType: "warning",
      message: `Wynik jest poprawny, ale można go uprościć: $${beforeLatex} = ${afterLatex}$.`,
    };
  }

  // jeśli nie da się uprościć, to traktujemy jako pełną poprawność
  return true;
}


// ==== ANSWER / EXPLANATION ====

export function renderAnswer({ data }) {
  return <InlineMath math={String(data?.sqrt ?? "")} />;
}

export function renderExplanation({ data }) {
  const { x1, y1, x2, y2 } = data;
  const dx = x2 - x1;
  const dy = y2 - y1;
  const dx2 = dx ** 2;
  const dy2 = dy ** 2;
  const pierw = dx2 + dy2;
  const sqrt = simplifySqrt(pierw);

  const fs = (n) => (n < 0 ? `(${n})` : `${n}`);

  return (
    <div className="space-y-2">
      <p>
        <strong>Krok 1:</strong> Korzystamy ze wzoru na długość odcinka:
      </p>
      <BlockMath math="|AB| = \sqrt{(x_B - x_A)^2 + (y_B - y_A)^2}" />

      <p>
        <strong>Krok 2:</strong> Podstawiamy dane:
      </p>
      <BlockMath math={`|AB| = \\sqrt{(${fs(x2)} - ${fs(x1)})^2 + (${fs(y2)} - ${fs(y1)})^2}`} />

      <p>
        <strong>Krok 3:</strong> Obliczamy różnice i kwadraty:
      </p>
      <BlockMath math={`|AB| = \\sqrt{${fs(dx)}^2 + ${fs(dy)}^2} = \\sqrt{${dx2} + ${dy2}}`} />

      <p>
        <strong>Krok 4:</strong> Sumujemy pod pierwiastkiem i upraszczamy:
      </p>
      {sqrt === `\\sqrt{${pierw}}` ? (
        <BlockMath math={`|AB| = \\sqrt{${pierw}}`} />
      ) : (
        <BlockMath math={`|AB| = \\sqrt{${pierw}} = ${sqrt}`} />
      )}
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  const s = coerceToString(userAnswer);
  if (s.trim() === "") return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={s} />;
}
